import React, { useMemo, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import useTableList from '../../utils/useTableList';
import { STATUS_OPTIONS } from '../../constants/listOptions';
import ConfirmDialog from '../../components/ConfirmDialog';
import Drawer from '../../components/Drawer';
import useEditRoute from '../../utils/useEditRoute';
import usePageTitle from '../../utils/usePageTitle';
import { warehousesService } from '../../services/productionService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

function WarehousesList() {
    usePageTitle('Depósitos');
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canWarehouses = can('Warehouses');

    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingWarehouse, setEditingWarehouse] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        status: 'active',
    });

    const list = useTableList({
        storageKey: 'warehouses',
        fetcher: (params) => warehousesService.list(params),
        errorMessage: 'Não foi possível carregar os depósitos',
    });

    const columns = useMemo(() => ([
        {
            key: 'name',
            label: 'Nome',
            className: 'font-medium',
        },
        {
            key: 'description',
            label: 'Descrição',
            render: (value) => value || '-',
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => {
                const map = {
                    active: { label: 'Ativo', className: 'bg-green-100 text-green-800' },
                    inactive: { label: 'Inativo', className: 'bg-gray-100 text-gray-800' },
                };
                const cfg = map[value] || { label: value || '-', className: 'bg-gray-100 text-gray-800' };
                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.className}`}>
                        {cfg.label}
                    </span>
                );
            },
        },
    ]), []);

    const filters = [
        { key: 'name', label: 'Nome', type: 'text' },
        { key: 'description', label: 'Descrição', type: 'text' },
        { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
    ];

    const openNew = () => {
        setEditingWarehouse(null);
        setFormData({
            name: '',
            description: '',
            status: 'active',
        });
        setDrawerOpen(true);
    };

    const openEdit = async (warehouse) => {
        setFormLoading(true);
        try {
            const response = await warehousesService.get(warehouse.id);
            const data = response.data?.data || response.data || warehouse;
            setEditingWarehouse(data);
            setFormData({
                name: data.name || '',
                description: data.description || '',
                status: data.status || 'active',
            });
            setDrawerOpen(true);
        } catch (e) {
            console.error('Error loading warehouse:', e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar o depósito' });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteClick = (warehouse) => {
        setDeleteDialog({ open: true, id: warehouse.id });
    };

    const handleDeleteConfirm = async () => {
        try {
            await warehousesService.delete(deleteDialog.id);
            setDeleteDialog({ open: false, id: null });
            list.reload();
            toast({
                title: 'Depósito excluído',
                description: 'O depósito foi excluído com sucesso',
                variant: 'success',
            });
        } catch (e) {
            console.error('Error deleting warehouse:', e);
            toastApiError(e, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir o depósito' });
        }
    };

    const handleFormChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleSave = async () => {
        if (formLoading) return;
        setFormLoading(true);
        try {
            const payload = {
                name: formData.name,
                description: formData.description,
                status: formData.status,
            };

            if (editingWarehouse?.id) {
                const response = await warehousesService.update(editingWarehouse.id, payload);
                const saved = response.data?.data || response.data;
                if (saved) setEditingWarehouse(saved);
                toast({
                    title: 'Depósito salvo',
                    description: 'Alterações salvas com sucesso',
                    variant: 'success',
                });
            } else {
                const response = await warehousesService.create(payload);
                const saved = response.data?.data || response.data;
                if (saved) setEditingWarehouse(saved);
                toast({
                    title: 'Depósito criado',
                    description: 'Cadastro realizado com sucesso',
                    variant: 'success',
                });
            }

            list.reload();
        } catch (e) {
            console.error('Error saving warehouse:', e);
            toastApiError(e, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar o depósito' });
        } finally {
            setFormLoading(false);
        }
    };

    useEditRoute({
        basePath: '/settings/warehouses',
        ready: list.loaded,
        drawerOpen: drawerOpen,
        open: id => openEdit({ id }),
    });

    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title="Depósitos"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Configurações' },
                    ]}
                    actions={[
                        ...(canWarehouses ? [{
                            label: 'Novo depósito',
                            icon: 'new',
                            onClick: openNew,
                        }] : []),
                    ]}
                />

                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por nome..."
                    columns={columns}
                    editTo={canWarehouses ? (row) => `/settings/warehouses/${row.id}` : undefined}
                    onEdit={canWarehouses ? openEdit : undefined}
                    onDelete={canWarehouses ? handleDeleteClick : undefined}
                    emptyMessage="Nenhum depósito cadastrado"
                />
            </div>

            <ConfirmDialog
                open={deleteDialog.open}
                title="Excluir depósito"
                message="Tem certeza que deseja excluir este depósito? Esta ação não pode ser desfeita."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, id: null })}
            />

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={editingWarehouse?.id ? 'Editar depósito' : 'Novo depósito'}
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="button" onClick={handleSave} disabled={formLoading || !canWarehouses}>
                            {formLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome *
                        </label>
                        <Input
                            name="name"
                            value={formData.name}
                            onChange={handleFormChange}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Descrição
                        </label>
                        <Input
                            name="description"
                            value={formData.description}
                            onChange={handleFormChange}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status *
                        </label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleFormChange}
                            className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        >
                            <option value="active">Ativo</option>
                            <option value="inactive">Inativo</option>
                        </select>
                    </div>
                </div>
            </Drawer>
        </AppLayout>
    );
}

export default WarehousesList;

